import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import CTAButton from '../components/CTAButton';
import StatusBadge from '../components/StatusBadge';
import ReusableCodeExecutor from '../components/ReusableCodeExecutor';
import { useSeo } from '../utils/seo';
import { javaApi } from '../services/api';

const LandingPage = () => {
  useSeo(
    'Code by Skillora - Free Online Java Compiler | Quantum Stack',
    'Code by Skillora - Best free online Java compiler and code executor by Quantum Stack. Run Java code instantly in your browser. No installation required. Practice Java programming with our powerful online IDE. Skillora code editor for developers.'
  );

  const [code, setCode] = useState(`public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
        System.out.println("Welcome to Java Code Executor!");
    }
}`);
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Example code snippets for "Try Example" button
  const exampleCodes = [
    {
      name: "Hello World",
      code: `public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
        System.out.println("Welcome to Java Code Executor!");
    }
}`
    },
    {
      name: "Calculator",
      code: `public class Calculator {
    public static void main(String[] args) {
        int a = 10;
        int b = 20;
        int sum = a + b;
        System.out.println("Sum: " + sum);
        System.out.println("Difference: " + (a - b));
        System.out.println("Product: " + (a * b));
    }
}`
    },
    {
      name: "Factorial",
      code: `public class Factorial {
    public static void main(String[] args) {
        int n = 5;
        int factorial = 1;
        
        for (int i = 1; i <= n; i++) {
            factorial *= i;
        }
        
        System.out.println("Factorial of " + n + " is: " + factorial);
    }
}`
    }
  ];

  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);

  const handleCodeChange = (e) => {
    setCode(e.target.value);
    if (validationError) {
      setValidationError('');
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleRun = async () => {
    if (!code.trim()) {
      setValidationError('Please enter some Java code to execute.');
      return;
    }

    setValidationError('');
    setIsLoading(true);
    setOutput('Executing code...');
    setStatus('');

    try {
      const response = await javaApi.executeCode(code, input);
      setOutput(response.output || 'No output');
      setStatus(response.status || 'success');
      
      if (!response.success) {
        setStatus(response.status || 'error');
      }
    } catch (error) {
      console.error('Execution failed:', error);
      
      if (error.response) {
        const { status: statusCode, data } = error.response;
        if (statusCode === 400) {
          setOutput(data.output || 'Bad request: Invalid input');
          setStatus('error');
        } else if (statusCode >= 500) {
          setOutput('Server error: Please try again later');
          setStatus('error');
        } else {
          setOutput(data.output || `Error: ${statusCode}`);
          setStatus(data.status || 'error');
        }
      } else if (error.request) {
        setOutput('Network error: Unable to connect to server');
        setStatus('error');
      } else {
        setOutput('Unexpected error: Please try again');
        setStatus('error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTryExample = () => {
    const nextIndex = (currentExampleIndex + 1) % exampleCodes.length;
    setCurrentExampleIndex(nextIndex);
    setCode(exampleCodes[nextIndex].code);
    setOutput('');
    setStatus('');
    setValidationError('');
  };

  return (
    <Layout>
      <div className="landing-page">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-container">
            <img src="/logo.png" alt="Code by Skillora" className="hero-logo" />
            <h1>Online Java Compiler</h1>
            <p className="hero-subheading">
              Compile and run Java code directly in your browser with our free online Java compiler. 
              No installation required - perfect for learning, testing, and quick prototyping.
            </p>
            <div className="hero-cta">
              <CTAButton to="#code-editor">Run Code Now</CTAButton>
              <CTAButton to="/java-playground" primary={false}>Try Playground</CTAButton>
              <CTAButton to="https://skillora.stacknora.com" primary={false} target="_blank">Visit Skillora</CTAButton>
            </div>
          </div>
        </section>

        {/* What is an Online Java Compiler Section */}
        <section className="info-section">
          <div className="info-container">
            <h2>What is an Online Java Compiler</h2>
            <p>
              An online Java compiler is a web-based tool that lets you write, compile, and execute Java code 
              directly in your browser without installing any software. Our platform handles the entire 
              compilation process automatically, converting your Java source code into bytecode and running 
              it in a secure environment. This makes it perfect for students learning Java, developers 
              testing code snippets, or anyone needing quick Java code execution.
            </p>
          </div>
        </section>

        {/* How to Run Java Code Online Section */}
        <section className="info-section">
          <div className="info-container">
            <h2>How to Run Java Code Online</h2>
            <p>
              Running Java code online is simple with our compiler. Just type your Java code in the editor, 
              add any input if needed, and click the Run button. Our system compiles and executes your code 
              instantly, showing you the output in real-time. No setup, no configuration, no hassle - just 
              pure Java coding in your browser.
            </p>
          </div>
        </section>

        {/* Main Section - Code Editor */}
        <section id="code-editor" className="editor-section">
          <div className="editor-container">
            <h2>Try It Now - Write Your Java Code</h2>
            <ReusableCodeExecutor 
              defaultCode={`public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
        System.out.println("Welcome to Code by Skillora!");
    }
}`}
            />
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <div className="features-container">
            <h2>Why Choose Our Java Code Executor?</h2>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">⚡</div>
                <h3>Fast Execution</h3>
                <p>Compile and run your Java code in seconds with our optimized cloud infrastructure.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🚀</div>
                <h3>No Installation</h3>
                <p>Start coding immediately in your browser. No JDK setup, no configuration, no hassle.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🔒</div>
                <h3>Safe Sandbox</h3>
                <p>Your code runs in isolated Docker containers with strict security controls.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📱</div>
                <h3>Works Anywhere</h3>
                <p>Access your Java coding environment from any device with an internet connection.</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="how-it-works-section">
          <div className="how-it-works-container">
            <h2>How It Works</h2>
            <div className="steps-grid">
              <div className="step-card">
                <div className="step-number">1</div>
                <h3>Write Code</h3>
                <p>Type your Java code in our editor with syntax highlighting and auto-formatting.</p>
              </div>
              <div className="step-card">
                <div className="step-number">2</div>
                <h3>Run Code</h3>
                <p>Click the Run button to compile and execute your code instantly.</p>
              </div>
              <div className="step-card">
                <div className="step-number">3</div>
                <h3>See Output</h3>
                <p>View results, error messages, and execution status in real-time.</p>
              </div>
            </div>
            <div className="how-it-works-cta">
              <CTAButton to="#code-editor">Get Started Now</CTAButton>
            </div>
          </div>
        </section>

        {/* Footer Section */}
        <footer className="landing-footer">
          <div className="footer-container">
            <div className="footer-content">
              <div className="footer-section">
                <h3>Java Code Executor</h3>
                <p>Fast, secure, and reliable Java code execution platform.</p>
              </div>
              <div className="footer-section">
                <h4>Learn More</h4>
                <ul>
                  <li><Link to="/java-online-compiler">Online Java Compiler</Link></li>
                  <li><Link to="/run-java-code-online">Run Java Code Online</Link></li>
                  <li><Link to="/java-playground">Java Playground</Link></li>
                </ul>
              </div>
              <div className="footer-section">
                <h4>Resources</h4>
                <ul>
                  <li><Link to="/java-online-compiler">Java Tutorial</Link></li>
                  <li><Link to="/run-java-code-online">Code Examples</Link></li>
                  <li><Link to="/java-playground">Practice Problems</Link></li>
                </ul>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2024 Java Code Executor. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </Layout>
  );
};

export default LandingPage;
